/**
 * Business type and business modules.
 *
 * One codebase runs a supermarket, a hardware shop or a bistro. Which one a business is, and which
 * modules it runs, is DATA set by the platform operator (SUPERADMIN), never an `if` in shared code:
 * a screen, a till button or an API route asks `isModuleEnabled`, and the API refuses a module that is
 * switched off (hiding it in the UI is only cosmetic). See docs/Extensibility.md §3.1.
 *
 * Three switches decide whether a module is on, all of which must agree:
 *   - status   it is built (`available`), not just on the roadmap (`planned`);
 *   - plan     the store's plan (BASIC < PRO < ADVANCED) includes it (`minPlan`): what the store pays for;
 *   - toggle   the operator has not switched it off for this business.
 * Business type only picks which modules a store STARTS with (`defaultFor`); it never gates on its own.
 *
 * Stored as system settings so the till reads them in the one settings query it already makes:
 *   platform.business_type        SUPERMARKET | HARDWARE | BISTRO
 *   platform.plan                 BASIC | PRO | ADVANCED
 *   platform.module.<code>        'true' | 'false'
 * Store administrators cannot edit `platform.*` keys; only `PUT /api/platform/profile` can.
 *
 * Append-only: a module code, once shipped, keeps its meaning forever. A `planned` module is listed so
 * the operator sees the roadmap, but it cannot be switched on until it is built and marked `available`.
 */

export const BUSINESS_TYPES = ['SUPERMARKET', 'HARDWARE', 'BISTRO'] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

export const DEFAULT_BUSINESS_TYPE: BusinessType = 'SUPERMARKET';
export const BUSINESS_TYPE_SETTING = 'platform.business_type';
export const PLATFORM_SETTING_PREFIX = 'platform.';

export const PLANS = ['BASIC', 'PRO', 'ADVANCED'] as const;
export type Plan = (typeof PLANS)[number];
export const DEFAULT_PLAN: Plan = 'BASIC';
export const PLAN_SETTING = 'platform.plan';

export type ModuleStatus = 'available' | 'planned';

export interface BusinessModuleSpec {
  code: string;
  label: string;
  description: string;
  status: ModuleStatus;
  /** Business types that get this module switched on when the operator applies the type's defaults. */
  defaultFor: readonly BusinessType[];
  /** The lowest plan that includes it. Core shop features are BASIC; paid intelligence is PRO/ADVANCED. */
  minPlan: Plan;
}

const ALL: readonly BusinessType[] = BUSINESS_TYPES;

export const BUSINESS_MODULES = [
  // Built today: switching one off hides its screens and the API refuses its routes.
  { code: 'purchasing', label: 'Purchasing', description: 'Purchase orders, goods receipts and supplier returns', status: 'available', defaultFor: ALL, minPlan: 'BASIC' },
  { code: 'credit', label: 'Store credit', description: 'Sell on account and collect the balance later', status: 'available', defaultFor: ['SUPERMARKET', 'HARDWARE'], minPlan: 'BASIC' },
  { code: 'csvImport', label: 'CSV import', description: 'Bulk-load products and customers from a spreadsheet', status: 'available', defaultFor: ALL, minPlan: 'BASIC' },
  // Owner intelligence (docs/OwnerIntelligence.md). Register a module when its build starts, not before.
  { code: 'marginAlerts', label: 'Cost and margin alerts', description: 'Warns when a supplier raises a cost, with the margin effect and a suggested price', status: 'available', defaultFor: ALL, minPlan: 'BASIC' },
  // Roadmap (docs/Extensibility.md §4): listed, not switchable yet.
  { code: 'quotations', label: 'Quotations and estimates', description: 'Quote a price, convert it to an invoice later', status: 'planned', defaultFor: ['HARDWARE'], minPlan: 'BASIC' },
  { code: 'variants', label: 'Product variants', description: 'Size, colour or finish as one product family', status: 'planned', defaultFor: ['HARDWARE', 'BISTRO'], minPlan: 'BASIC' },
  { code: 'serials', label: 'Serial numbers and warranty', description: 'Track each unit sold by its serial', status: 'planned', defaultFor: ['HARDWARE'], minPlan: 'BASIC' },
  { code: 'altUnits', label: 'Buy and sell in different units', description: 'Buy by the box or roll, sell by the piece or metre', status: 'planned', defaultFor: ['SUPERMARKET', 'HARDWARE'], minPlan: 'BASIC' },
  { code: 'orders', label: 'Open orders and split bills', description: 'Orders that stay open until paid, split by item, seat or evenly', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'tables', label: 'Tables and floor plan', description: 'Seat guests, move and merge tables', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'kitchen', label: 'Kitchen display and printers', description: 'Send items to kitchen screens and ticket printers', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'modifiers', label: 'Add-ons and modifiers', description: 'Extra cheese, less sugar, no onion', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'recipes', label: 'Recipes', description: 'Deduct ingredients from stock when a dish is sold', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'serviceCharge', label: 'Service charge', description: 'An optional, taxable charge on the bill', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'tips', label: 'Tips', description: 'Record tips separately from revenue', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'takeaway', label: 'Takeaway and delivery', description: 'Parcel orders, delivery address, rider and charges', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'aggregators', label: 'Swiggy and Zomato orders', description: 'Aggregator orders with their commission tracked', status: 'planned', defaultFor: ['BISTRO'], minPlan: 'BASIC' },
  { code: 'upiReconciliation', label: 'UPI soundbox confirm and reconciliation', description: 'Confirm UPI by the soundbox, match payments with the bank statement at day end', status: 'planned', defaultFor: ['SUPERMARKET', 'HARDWARE', 'BISTRO'], minPlan: 'PRO' },
  { code: 'onlineOrdering', label: 'Online and mobile ordering', description: 'Customers order from their phone', status: 'planned', defaultFor: [], minPlan: 'ADVANCED' },
  // No gateway for now (docs/Extensibility.md §3.5): counter UPI is dynamic QR + soundbox + static QR.
  { code: 'gatewayPayments', label: 'Online payment gateway', description: 'Payment links and online checkout (not planned yet)', status: 'planned', defaultFor: [], minPlan: 'ADVANCED' },
] as const satisfies readonly BusinessModuleSpec[];

export type ModuleCode = (typeof BUSINESS_MODULES)[number]['code'];

export const MODULE_CODES: readonly ModuleCode[] = BUSINESS_MODULES.map((module) => module.code);

export function isPlan(value: unknown): value is Plan {
  return typeof value === 'string' && (PLANS as readonly string[]).includes(value);
}

/** Whether `plan` includes a module that needs `minPlan`. */
export function planIncludes(plan: Plan, minPlan: Plan): boolean {
  return PLANS.indexOf(plan) >= PLANS.indexOf(minPlan);
}

export function isBusinessType(value: unknown): value is BusinessType {
  return typeof value === 'string' && (BUSINESS_TYPES as readonly string[]).includes(value);
}

export function isModuleCode(value: unknown): value is ModuleCode {
  return typeof value === 'string' && (MODULE_CODES as readonly string[]).includes(value);
}

export function businessModule(code: string): BusinessModuleSpec | undefined {
  return BUSINESS_MODULES.find((module) => module.code === code);
}

export function moduleSettingKey(code: ModuleCode): string {
  return `${PLATFORM_SETTING_PREFIX}module.${code}`;
}

/** The modules a business type starts with. Only available modules can be on. */
export function defaultModulesFor(type: BusinessType): Record<ModuleCode, boolean> {
  return Object.fromEntries(
    BUSINESS_MODULES.map((module) => [
      module.code,
      module.status === 'available' && (module.defaultFor as readonly BusinessType[]).includes(type),
    ]),
  ) as Record<ModuleCode, boolean>;
}

export interface BusinessProfile {
  businessType: BusinessType;
  plan: Plan;
  /** Codes of the modules that are on: available, included in the plan, and not switched off. */
  enabledModules: ModuleCode[];
}

/**
 * Reads the profile from settings. A missing value means "as before": a supermarket on the BASIC plan
 * with every available BASIC module on, so a database that predates this keeps working unchanged.
 * A planned module, or one above the plan, is never on, whatever its toggle says.
 */
export function resolveBusinessProfile(read: (key: string) => string | null | undefined): BusinessProfile {
  const stored = read(BUSINESS_TYPE_SETTING);
  const businessType = isBusinessType(stored) ? stored : DEFAULT_BUSINESS_TYPE;
  const storedPlan = read(PLAN_SETTING);
  const plan = isPlan(storedPlan) ? storedPlan : DEFAULT_PLAN;
  const enabledModules = BUSINESS_MODULES.filter((module: BusinessModuleSpec) => {
    if (module.status !== 'available' || !planIncludes(plan, module.minPlan)) return false;
    const value = read(moduleSettingKey(module.code as ModuleCode));
    return value === null || value === undefined ? true : value === 'true';
  }).map((module) => module.code as ModuleCode);
  return { businessType, plan, enabledModules };
}

export function isModuleEnabled(profile: Pick<BusinessProfile, 'enabledModules'> | null | undefined, code: ModuleCode): boolean {
  return Boolean(profile?.enabledModules.includes(code));
}
